import React, { useRef } from "react";
import * as XLSX from "xlsx";
import { generatePDFFromElement } from "../../../../src/utils/invoiceUtils";

const WiserAdviserStudentReport = ({ 
  isOpen, 
  onClose, 
  sectionName, 
  className,
  campusName
}) => {
  const reportRef = useRef();

  // Generate dummy data for Wiser-Adviser Student report
  const generateWiserAdviserStudentData = () => {
    const students = [
      { id: "ST001", name: "Ahmed Ali", role: "Student" },
      { id: "ST002", name: "Fatima Khan", role: "Student" },
      { id: "ST003", name: "Hassan Sheikh", role: "Student" },
      { id: "ST004", name: "Aisha Malik", role: "Student" },
      { id: "ST005", name: "Omar Farooq", role: "Student" },
      { id: "ST006", name: "Zainab Ahmed", role: "Student" },
      { id: "ST007", name: "Yusuf Hassan", role: "Student" },
      { id: "ST008", name: "Maryam Khan", role: "Student" }
    ];

    const topics = [
      "Homework Help", "Study Strategies", "Subject Clarification", 
      "Exam Preparation", "Time Management", "Learning Techniques",
      "Problem Solving", "Research Methods", "Project Guidance",
      "Academic Planning", "Career Guidance", "Skill Development"
    ];

    return students.map(student => {
      const dailySessions = Math.floor(Math.random() * 3) + 1; // 1-3 sessions per day
      const weeklySessions = dailySessions * 5; // 5 working days
      const monthlySessions = weeklySessions * 4; // 4 weeks
      
      const totalPrompts = Math.floor(Math.random() * 150) + 30; // 30-180 prompts
      const avgPromptsPerSession = Math.round(totalPrompts / monthlySessions);
      
      const totalTimeMinutes = Math.floor(Math.random() * 600) + 120; // 2-10 hours
      const avgTimePerSession = Math.round(totalTimeMinutes / monthlySessions);
      
      // Generate top 5 topics
      const shuffledTopics = [...topics].sort(() => 0.5 - Math.random());
      const topTopics = shuffledTopics.slice(0, 5).map(topic => ({
        topic,
        count: Math.floor(Math.random() * 15) + 3
      }));

      return {
        ...student,
        dailySessions,
        weeklySessions,
        monthlySessions,
        totalPrompts,
        avgPromptsPerSession,
        totalTimeMinutes,
        avgTimePerSession,
        topTopics
      };
    });
  };

  const wiserAdviserStudentData = generateWiserAdviserStudentData();
  const academicYear = "2023-24";

  // Calculate summary statistics
  const totalStudents = wiserAdviserStudentData.length;
  const totalSessions = wiserAdviserStudentData.reduce((sum, student) => sum + student.monthlySessions, 0);
  const totalPrompts = wiserAdviserStudentData.reduce((sum, student) => sum + student.totalPrompts, 0);
  const avgPromptsPerSession = Math.round(totalPrompts / totalSessions);
  const totalTimeSpent = wiserAdviserStudentData.reduce((sum, student) => sum + student.totalTimeMinutes, 0);
  const avgTimePerSession = Math.round(totalTimeSpent / totalSessions);

  // Get top 5 topics across all students
  const allTopics = {};
  wiserAdviserStudentData.forEach(student => {
    student.topTopics.forEach(({ topic, count }) => {
      allTopics[topic] = (allTopics[topic] || 0) + count;
    });
  });
  const top5Topics = Object.entries(allTopics)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5)
    .map(([topic, count]) => ({ topic, count }));

  const downloadPDF = () => {
    generatePDFFromElement(
      reportRef.current,
      "Wiser_Adviser_Student_Report.pdf"
    );
  };

  const downloadExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // Summary sheet
    const summaryData = [
      ["Wiser-Adviser Student Report Summary"],
      ["Campus", campusName],
      ["Class", className],
      ["Section", sectionName],
      ["Academic Year", academicYear],
      ["Report Date", new Date().toLocaleDateString('en-GB')],
      [],
      ["Metric", "Value"],
      ["Total Students", totalStudents],
      ["Total Sessions (Monthly)", totalSessions],
      ["Total Prompts Submitted", totalPrompts],
      ["Average Prompts per Session", avgPromptsPerSession],
      ["Total Time Spent (Minutes)", totalTimeSpent],
      ["Average Time per Session (Minutes)", avgTimePerSession],
      [],
      ["Top 5 Topics", "Count"],
      ...top5Topics.map(({ topic, count }) => [topic, count])
    ];
    
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    // Detailed student data sheet
    const detailedData = [
      ["Detailed Student Data"],
      ["Campus", campusName],
      ["Class", className],
      ["Section", sectionName],
      [],
      ["Student ID", "Student Name", "Daily Sessions", "Weekly Sessions", "Monthly Sessions", "Total Prompts", "Avg Prompts/Session", "Total Time (Min)", "Avg Time/Session (Min)"]
    ];

    wiserAdviserStudentData.forEach(student => {
      detailedData.push([
        student.id,
        student.name,
        student.dailySessions,
        student.weeklySessions,
        student.monthlySessions,
        student.totalPrompts,
        student.avgPromptsPerSession,
        student.totalTimeMinutes,
        student.avgTimePerSession
      ]);
    });

    const detailedSheet = XLSX.utils.aoa_to_sheet(detailedData);
    XLSX.utils.book_append_sheet(workbook, detailedSheet, "Student Details");
    
    XLSX.writeFile(workbook, "Wiser_Adviser_Student_Report.xlsx");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div ref={reportRef} className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-h-[90vh] overflow-y-auto relative border border-gray-300">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-gray-800">{campusName}</h1>
          <h2 className="text-lg font-semibold mt-2">Wiser-Adviser Student Report</h2>
          <p className="text-base">{academicYear}</p>
          <p className="text-base font-medium">{className} - {sectionName}</p>
          <p className="text-sm text-gray-600">Report Generated: {new Date().toLocaleDateString('en-GB')}</p>
        </div>

        {/* Summary Statistics */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-center">Summary Statistics</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-gray-400 p-4">
              <h4 className="font-semibold mb-2">Total Students</h4>
              <div className="text-2xl font-bold text-blue-600">{totalStudents}</div>
            </div>
            <div className="border border-gray-400 p-4">
              <h4 className="font-semibold mb-2">Total Sessions (Monthly)</h4>
              <div className="text-2xl font-bold text-green-600">{totalSessions}</div>
            </div>
            <div className="border border-gray-400 p-4">
              <h4 className="font-semibold mb-2">Total Prompts Submitted</h4>
              <div className="text-2xl font-bold text-purple-600">{totalPrompts}</div>
            </div>
            <div className="border border-gray-400 p-4">
              <h4 className="font-semibold mb-2">Average Prompts per Session</h4>
              <div className="text-2xl font-bold text-orange-600">{avgPromptsPerSession}</div>
            </div>
            <div className="border border-gray-400 p-4">
              <h4 className="font-semibold mb-2">Total Time Spent (Minutes)</h4>
              <div className="text-2xl font-bold text-red-600">{totalTimeSpent}</div>
            </div>
            <div className="border border-gray-400 p-4">
              <h4 className="font-semibold mb-2">Average Time per Session (Minutes)</h4>
              <div className="text-2xl font-bold text-indigo-600">{avgTimePerSession}</div>
            </div>
          </div>
        </div>

        {/* Top 5 Topics */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-center">Top 5 Topics of Prompts</h3>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-400 p-2 text-left">Rank</th>
                <th className="border border-gray-400 p-2 text-left">Topic</th>
                <th className="border border-gray-400 p-2 text-center">Count</th>
              </tr>
            </thead>
            <tbody>
              {top5Topics.map(({ topic, count }, index) => (
                <tr key={index}>
                  <td className="border border-gray-400 p-2 text-center font-semibold">{index + 1}</td>
                  <td className="border border-gray-400 p-2">{topic}</td>
                  <td className="border border-gray-400 p-2 text-center">
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                      {count}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Detailed Student Data */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-center">Detailed Student Data</h3>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-400 p-2 text-left">Student ID</th>
                <th className="border border-gray-400 p-2 text-left">Student Name</th>
                <th className="border border-gray-400 p-2 text-center">Daily Sessions</th>
                <th className="border border-gray-400 p-2 text-center">Weekly Sessions</th>
                <th className="border border-gray-400 p-2 text-center">Monthly Sessions</th>
                <th className="border border-gray-400 p-2 text-center">Total Prompts</th>
                <th className="border border-gray-400 p-2 text-center">Avg Prompts/Session</th>
                <th className="border border-gray-400 p-2 text-center">Total Time (Min)</th>
                <th className="border border-gray-400 p-2 text-center">Avg Time/Session (Min)</th>
              </tr>
            </thead>
            <tbody>
              {wiserAdviserStudentData.map((student, index) => (
                <tr key={index}>
                  <td className="border border-gray-400 p-2">{student.id}</td>
                  <td className="border border-gray-400 p-2 font-medium">{student.name}</td>
                  <td className="border border-gray-400 p-2 text-center">{student.dailySessions}</td>
                  <td className="border border-gray-400 p-2 text-center">{student.weeklySessions}</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold">{student.monthlySessions}</td>
                  <td className="border border-gray-400 p-2 text-center">{student.totalPrompts}</td>
                  <td className="border border-gray-400 p-2 text-center">{student.avgPromptsPerSession}</td>
                  <td className="border border-gray-400 p-2 text-center">{student.totalTimeMinutes}</td>
                  <td className="border border-gray-400 p-2 text-center">{student.avgTimePerSession}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Individual Student Topics */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-center">Individual Student Topics</h3>
          <div className="space-y-6">
            {wiserAdviserStudentData.map((student, studentIndex) => (
              <div key={studentIndex} className="border border-gray-300 p-4 rounded">
                <h4 className="font-semibold mb-3 text-gray-700">
                  {student.name} ({student.id})
                </h4>
                <div className="grid grid-cols-5 gap-2">
                  {student.topTopics.map(({ topic, count }, topicIndex) => (
                    <div key={topicIndex} className="bg-gray-50 p-2 rounded text-center">
                      <div className="text-sm font-medium text-gray-600">{topic}</div>
                      <div className="text-lg font-bold text-green-600">{count}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Usage Analytics */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-center">Usage Analytics</h3>
          <div className="grid grid-cols-3 gap-6">
            <div className="border border-gray-400 p-4 text-center">
              <h4 className="font-semibold mb-2">Most Active Student</h4>
              <div className="text-lg font-bold text-blue-600">
                {wiserAdviserStudentData.reduce((max, student) => 
                  student.totalPrompts > max.totalPrompts ? student : max
                ).name}
              </div>
              <div className="text-sm text-gray-600">
                {wiserAdviserStudentData.reduce((max, student) => 
                  student.totalPrompts > max.totalPrompts ? student : max
                ).totalPrompts} prompts
              </div>
            </div>
            <div className="border border-gray-400 p-4 text-center">
              <h4 className="font-semibold mb-2">Highest Session Time</h4>
              <div className="text-lg font-bold text-green-600">
                {wiserAdviserStudentData.reduce((max, student) => 
                  student.totalTimeMinutes > max.totalTimeMinutes ? student : max
                ).name}
              </div>
              <div className="text-sm text-gray-600">
                {wiserAdviserStudentData.reduce((max, student) => 
                  student.totalTimeMinutes > max.totalTimeMinutes ? student : max
                ).totalTimeMinutes} minutes
              </div>
            </div>
            <div className="border border-gray-400 p-4 text-center">
              <h4 className="font-semibold mb-2">Most Consistent User</h4>
              <div className="text-lg font-bold text-purple-600">
                {wiserAdviserStudentData.reduce((max, student) => 
                  student.monthlySessions > max.monthlySessions ? student : max
                ).name}
              </div>
              <div className="text-sm text-gray-600">
                {wiserAdviserStudentData.reduce((max, student) => 
                  student.monthlySessions > max.monthlySessions ? student : max
                ).monthlySessions} sessions
              </div>
            </div>
          </div>
        </div>

        {/* Signature Lines */}
        <div className="grid grid-cols-3 gap-8 mt-12">
          <div className="text-center">
            <div className="border-t border-gray-400 mt-8 pt-2">
              Class Teacher's Signature
            </div>
          </div>
          <div className="text-center">
            <div className="border-t border-gray-400 mt-8 pt-2">
              Head of Department's Signature
            </div>
          </div>
          <div className="text-center">
            <div className="border-t border-gray-400 mt-8 pt-2">
              Principal's Signature
            </div>
          </div>
        </div>

        {/* Download Buttons */}
        <div className="no-print flex justify-end mt-6 space-x-4">
          <button onClick={downloadPDF} className="bg-blue-500 px-4 py-2 rounded text-white hover:bg-blue-600">
            Download as PDF
          </button>
          <button onClick={downloadExcel} className="bg-green-500 px-4 py-2 rounded text-white hover:bg-green-600">
            Download as Excel
          </button>
          <button onClick={onClose} className="bg-gray-400 px-4 py-2 rounded text-white hover:bg-gray-500">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default WiserAdviserStudentReport;
